import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Platform, StyleSheet, View } from 'react-native';

import { useOnBoarding } from '@/api/hooks/meHooks';
import { OnBoardingParams } from '@/api/models/meModels';
import BrandedButton from '@/components/buttons/BrandedButton';
import RangeSliderInputField from '@/components/inputs/RangeSliderInputField';
import Text from '@/components/Text';
import { MAXIMUM_DISTANCE, MINIMUM_DISTANCE } from '@/constants/layout';
import { useColor } from '@/hooks/useColor';
import { DistancesData, distancesResolver } from '@/lib/validations/AuthSchema';
import { i18n } from '@/utils/i18n';

import GoogleAutoComplete from '../inputs/GoogleAutocomplete';

interface DistancesFormProps {
  defaultValues: {
    location: OnBoardingParams['location'];
    searchRange: OnBoardingParams['range'];
    address?: string;
  };
  onBoardingId: string;
  submitLabel?: string;
  onSuccessFn: (data: DistancesData) => void;
  onFailureFn?: () => void;
}

const DistancesForm = ({
  defaultValues,
  onBoardingId,
  submitLabel = i18n.t('mix.next'),
  onSuccessFn,
  onFailureFn
}: DistancesFormProps) => {
  const colors = useColor();
  const { mutateAsync: onBoardingUpdate, isPending } = useOnBoarding();
  const [city, setCity] = useState<string | undefined>(defaultValues.address);

  //Locales
  const descriptionText = i18n.t('sign_in.individual.distances.description');
  const placeholderText = i18n.t('sign_in.individual.distances.placeholder');
  const rangeText = i18n.t('sign_in.individual.distances.range', {
    MINIMUM_DISTANCE: MINIMUM_DISTANCE,
    MAXIMUM_DISTANCE: MAXIMUM_DISTANCE
  });

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { isValid, submitCount, errors }
  } = useForm<DistancesData>({
    defaultValues: {
      location: defaultValues?.location,
      distances: defaultValues?.searchRange ?? 150
    },
    mode: 'onChange',
    resolver: distancesResolver as any
  });

  const onSubmit: SubmitHandler<DistancesData> = async (data) => {
    await onBoardingUpdate(
      { range: data.distances, location: data.location, onBoardingId: onBoardingId, address: city },
      {
        onSuccess: (data) => onSuccessFn(data),
        onError: (_) => {
          onFailureFn && onFailureFn();
        }
      }
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.inputContent}>
        <View style={styles.autoComplete}>
          <GoogleAutoComplete
            initialValue={defaultValues.address}
            type="city"
            placeholder={placeholderText}
            error={errors.location}
            onChange={(result) => {
              if (result) {
                clearErrors('location');
                setValue('location', `${result.lat},${result.lng}`, { shouldValidate: true });
                setCity(result.formattedAddress);
              } else {
                setError('location', {
                  type: 'required',
                  message: i18n.t('mix.city_required')
                });
                setCity(undefined);
              }
            }}
          />
        </View>

        <Text style={[styles.descriptionText, { color: colors.titleText }]}>{descriptionText}</Text>

        <RangeSliderInputField
          control={control}
          name="distances"
          minimumValue={MINIMUM_DISTANCE}
          showError={false}
          maximumValue={MAXIMUM_DISTANCE}
          title={rangeText}
          step={1}
        />
      </View>

      <BrandedButton
        style={styles.btnStyle}
        loading={isPending}
        onPress={handleSubmit(onSubmit)}
        disabled={!isValid && submitCount > 0}>
        {submitLabel}
      </BrandedButton>
    </View>
  );
};

export default DistancesForm;

const styles = StyleSheet.create({
  contentWrapper: {
    flex: 1
  },

  container: {
    paddingHorizontal: 18,
    flexGrow: 1
  },

  titleText: {
    fontSize: 22,
    marginBottom: 25,
    textAlign: 'center',
    marginTop: Platform.select({ ios: 18, android: 28 })
  },

  descriptionText: {
    fontSize: 14
  },

  formContainer: {
    marginBottom: 20
  },

  btnStyle: {
    marginTop: 20,
    width: '100%'
  },

  btnText: {
    color: '#00000099',
    fontSize: 14
  },

  inputContent: {
    flex: 1,
    rowGap: 25
  },

  autoComplete: {
    zIndex: 1
  }
});
